import React, { useState } from 'react';
import Layout from '../components/Layout';
import { sendGridAPI } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Send, Key } from 'lucide-react';

const SendGridSettings = () => {
  const [testEmailData, setTestEmailData] = useState({
    from_email: '',
    to_email: '',
    subject: 'Test Email from Dommo Swiss Mailer',
    html_content: '<h1>Hello!</h1><p>This is a test email from Dommo Swiss Mailer.</p>',
    api_key: ''
  });
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    api_key: '',
    sender_email: '',
    sender_name: ''
  });

  const handleSendTest = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await sendGridAPI.testEmail(testEmailData);
      toast.success('Test email sent successfully!');
      setTestEmailData({ ...testEmailData, html_content: '<h1>Hello!</h1><p>This is a test email from Dommo Swiss Mailer.</p>' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Save to localStorage for now (could be saved to backend/domain)
      localStorage.setItem('sendgrid_settings', JSON.stringify(settings));
      toast.success('SendGrid settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    // Load saved settings
    const saved = localStorage.getItem('sendgrid_settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      setSettings(parsed);
      setTestEmailData(prev => ({
        ...prev,
        api_key: parsed.api_key || '',
        from_email: parsed.sender_email || '',
        from_name: parsed.sender_name || ''
      }));
    }
  }, []);

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
            SendGrid Settings
          </h1>
          <p className="text-lg text-[#9CA3AF]">
            Configure SendGrid integration and send test emails
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* SendGrid Configuration Form */}
          <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-3 mb-6">
              <Key size={24} className="text-[#183871]" strokeWidth={1.5} />
              <h2 className="text-2xl font-semibold tracking-tight text-[#F3F4F6]">
                SendGrid Configuration
              </h2>
            </div>

            <form onSubmit={handleSaveSettings} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="settings_api_key" className="text-[#F3F4F6]">
                  SendGrid API Key *
                </Label>
                <Input
                  id="settings_api_key"
                  data-testid="sendgrid-settings-api-key"
                  type="password"
                  placeholder="SG.xxxxxxxxxx"
                  value={settings.api_key}
                  onChange={(e) => {
                    setSettings({ ...settings, api_key: e.target.value });
                    setTestEmailData({ ...testEmailData, api_key: e.target.value });
                  }}
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF] font-mono text-sm"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sender_email" className="text-[#F3F4F6]">
                  Default Sender Email *
                </Label>
                <Input
                  id="sender_email"
                  data-testid="sender-email"
                  type="email"
                  placeholder="info@dommo.ch"
                  value={settings.sender_email}
                  onChange={(e) => {
                    setSettings({ ...settings, sender_email: e.target.value });
                    setTestEmailData({ ...testEmailData, from_email: e.target.value });
                  }}
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sender_name" className="text-[#F3F4F6]">
                  Default Sender Name
                </Label>
                <Input
                  id="sender_name"
                  data-testid="sender-name"
                  placeholder="Dommo Team"
                  value={settings.sender_name}
                  onChange={(e) => {
                    setSettings({ ...settings, sender_name: e.target.value });
                    setTestEmailData({ ...testEmailData, from_name: e.target.value });
                  }}
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                />
              </div>

              <Button
                type="submit"
                data-testid="save-sendgrid-settings"
                disabled={saving}
                className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white shadow-[0_0_15px_rgba(24,56,113,0.3)]"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </form>
          </div>

          {/* Test Email Form */}
          <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-3 mb-6">
              <Send size={24} className="text-[#183871]" strokeWidth={1.5} />
              <h2 className="text-2xl font-semibold tracking-tight text-[#F3F4F6]">
                Send Test Email
              </h2>
            </div>

            <form onSubmit={handleSendTest} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="from_email" className="text-[#F3F4F6]">
                  From Email
                </Label>
                <Input
                  id="from_email"
                  data-testid="from-email"
                  type="email"
                  placeholder="sender@example.com"
                  value={testEmailData.from_email}
                  onChange={(e) => setTestEmailData({ ...testEmailData, from_email: e.target.value })}
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="to_email" className="text-[#F3F4F6]">
                  To Email
                </Label>
                <Input
                  id="to_email"
                  data-testid="to-email"
                  type="email"
                  placeholder="recipient@example.com"
                  value={testEmailData.to_email}
                  onChange={(e) => setTestEmailData({ ...testEmailData, to_email: e.target.value })}
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subject" className="text-[#F3F4F6]">
                  Subject
                </Label>
                <Input
                  id="subject"
                  data-testid="email-subject"
                  type="text"
                  placeholder="Email subject"
                  value={testEmailData.subject}
                  onChange={(e) => setTestEmailData({ ...testEmailData, subject: e.target.value })}
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF]"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="html_content" className="text-[#F3F4F6]">
                  HTML Content
                </Label>
                <Textarea
                  id="html_content"
                  data-testid="email-html-content"
                  placeholder="<h1>Hello World</h1>"
                  value={testEmailData.html_content}
                  onChange={(e) => setTestEmailData({ ...testEmailData, html_content: e.target.value })}
                  className="bg-[#1F2128] border-[#2A2D36] text-[#F3F4F6] placeholder:text-[#9CA3AF] font-mono text-sm min-h-[200px]"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="send-test-email-button"
                disabled={sending}
                className="w-full bg-[#183871] hover:bg-[#1F4BA1] text-white shadow-[0_0_15px_rgba(24,56,113,0.3)]"
              >
                {sending ? 'Sending...' : 'Send Test Email'}
              </Button>
            </form>
          </div>

          {/* API Key Instructions */}
          <div className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm p-6 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)]">
            <div className="flex items-center gap-3 mb-6">
              <Key size={24} className="text-[#06B6D4]" strokeWidth={1.5} />
              <h2 className="text-2xl font-semibold tracking-tight text-[#F3F4F6]">
                Setup Instructions
              </h2>
            </div>

            <div className="space-y-4 text-[#9CA3AF]">
              <div>
                <h3 className="text-[#F3F4F6] font-semibold mb-2">1. Get Your SendGrid API Key</h3>
                <p className="text-sm leading-relaxed">
                  Visit <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-[#183871] hover:text-[#4B55AA]">SendGrid API Keys</a> and create a new API key with Full Access permissions.
                </p>
              </div>

              <div>
                <h3 className="text-[#F3F4F6] font-semibold mb-2">2. Verify Your Sender Identity</h3>
                <p className="text-sm leading-relaxed">
                  Before sending emails, verify your sender email address or domain in <a href="https://app.sendgrid.com/settings/sender_auth" target="_blank" rel="noopener noreferrer" className="text-[#183871] hover:text-[#4B55AA]">SendGrid Sender Authentication</a>.
                </p>
              </div>

              <div>
                <h3 className="text-[#F3F4F6] font-semibold mb-2">3. Test Your Configuration</h3>
                <p className="text-sm leading-relaxed">
                  Use the form on the left to send a test email and verify your SendGrid integration is working correctly.
                </p>
              </div>

              <div className="mt-6 p-4 rounded-md bg-[#183871]/10 border border-[#183871]/20">
                <p className="text-sm text-[#F3F4F6]">
                  <strong>Note:</strong> Keep your API keys secure and never share them publicly. For production use, store API keys in domain settings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SendGridSettings;
